import { isNullOrUndefined } from '@bimser/core';
import DataGrid2 from '..';
import { IDataGridColumn, IDataGridProperties, IDataGridRowCollection } from '../entities';
import {
	datePickerFormatter,
	dateTimePickerFormatter,
	timePickerFormatter
} from './columnFormatters';
import { IControlProperties } from '../../../../../entities';

export function loadProperties(this: DataGrid2, data: IDataGridProperties) {
	configureColumnFormatters.call(this, data.columns);

	this.__proxyChanged.columns = data.columns;
	this.__proxyChanged.dataSource = data.dataSource;
	this.__proxyChanged.dataSourceType = data.dataSourceType;
	this.__proxyChanged.columnSettings = data.columnSettings;
	this.__proxyChanged.detailRow = data.detailRow;
	this.__proxyChanged.editing = data.editing;
	this.__proxyChanged.filtering = data.filtering;
	this.__proxyChanged.grouping = data.grouping;
	this.__proxyChanged.paging = data.paging;
	this.__proxyChanged.searching = data.searching;
	this.__proxyChanged.selection = data.selection;
	this.__proxyChanged.sorting = data.sorting;
	this.__proxyChanged.toolbarActionButtons = data.toolbarActionButtons;
	this.__proxyChanged.height = data.height;
	this.__proxyChanged.displayType = data.displayType;
	this.__proxyChanged.alternateColor = data.alternateColor;
	this.__proxyChanged.virtualScrolling = data.virtualScrolling;
	this.__proxyChanged.exportToExcel = data.exportToExcel;
	this.__proxyChanged.showClearAllButton = data.showClearAllButton;
	this.__proxyChanged.gridLines = data.gridLines;
	this.__proxyChanged.gridBorder = data.gridBorder;
	this.__proxyChanged.hiddenRowColumnName = data.hiddenRowColumnName;
	this.__proxyChanged.hiddenRowValueMatchers = data.hiddenRowValueMatchers;
	this.__proxyChanged.batchOperations = data.batchOperations;
	this.__proxyChanged.hoverStateEnabled = data.hoverStateEnabled;
	this.__proxyChanged.selectedRowKeys = data.selectedRowKeys;
	this.__proxyChanged.selectedRowsData = data.selectedRowsData;
	this.__proxyChanged.columnAutoWidth = data.columnAutoWidth;
	this.__proxyChanged.rowDragging = data.rowDragging;
	this.__proxyChanged.wordWrapEnabled = data.wordWrapEnabled;
	this.__proxyChanged.columnHidingEnabled = data.columnHidingEnabled;
	this.__proxyChanged.columnFixingEnabled = data.columnFixingEnabled;
	this.__proxyChanged.selectionForm = data.selectionForm;
	this.__proxyChanged.saveMode = data.saveMode;
	this.__proxyChanged.cacheSettingsEnabled = data.cacheSettingsEnabled;
	this.__proxyChanged.rows = transformDataGridRows.call(this, data.columns, data.rows);

	this.__bindArrayStore();
}

function transformDataGridRows(
	this: DataGrid2,
	columns: IDataGridColumn<IControlProperties>[] = [],
	rows: IDataGridRowCollection = []
) {
	const objectComboColumns = new Set(
		columns.reduce((acc, c) => (c.dbType.type === 'object' ? [...acc, c.name] : acc), [])
	);

	return rows.map(row => ({
		...row,
		data: row.cells.reduce((acc, cell) => {
			if (objectComboColumns.has(cell.name)) {
				if (cell.editType === 'comboBox') {
					acc[cell.name] = {
						value: cell.value,
						text: cell.text,
						externalData: row.cellData[`__${cell.name}`] || {}
					};
				} else if (cell.editType === 'multiLanguageTextBox') {
					if (isNullOrUndefined(acc[cell.name])) {
						acc[cell.name] = {
							[this.__form.currentLanguage.culture]: cell.value
						};
					}
				}
			} else {
				acc[cell.name] = cell.value;
			}

			return acc;
		}, row.data)
	}));
}

function configureColumnFormatters(
	this: DataGrid2,
	columns: IDataGridColumn<IControlProperties>[] = []
) {
	columns.forEach(newCol => {
		const oldCol = this.columns.find(c => c.name === newCol.name);

		if (!oldCol || oldCol.format !== newCol.format) {
			switch (newCol.editType) {
				case 'dateTimePicker': {
					this.__formatterCache.set(newCol.name, dateTimePickerFormatter.bind(newCol));
					break;
				}
				case 'datePicker': {
					this.__formatterCache.set(newCol.name, datePickerFormatter.bind(newCol));
					break;
				}

				case 'timePicker': {
					this.__formatterCache.set(newCol.name, timePickerFormatter.bind(newCol));
					break;
				}
			}
		}
	});
}
